import React from 'react';
import { Sparkles, ArrowRight, Code, Presentation, Palette, Globe, TrendingUp, Handshake } from 'lucide-react';
import { VENTURE_LAB_OFFERINGS } from '../data/siteData';

interface VentureLabSectionProps {
  onOpenBooking: () => void;
  onExploreVentureLab: () => void;
  isPageHeader?: boolean;
}

export const VentureLabSection: React.FC<VentureLabSectionProps> = ({ onOpenBooking, onExploreVentureLab, isPageHeader }) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Code': return <Code className="h-5 w-5" />;
      case 'Presentation': return <Presentation className="h-5 w-5" />;
      case 'Palette': return <Palette className="h-5 w-5" />;
      case 'Globe': return <Globe className="h-5 w-5" />;
      case 'TrendingUp': return <TrendingUp className="h-5 w-5" />;
      case 'Handshake': return <Handshake className="h-5 w-5" />;
      default: return <Sparkles className="h-5 w-5" />;
    }
  };

  return (
    <section id="venture-lab" className="py-20 lg:py-28 bg-[#0b0c16] text-white border-b border-slate-800 relative overflow-hidden">
      {/* Glow Effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[600px] rounded-full bg-purple-600/10 blur-[120px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-500/30 bg-purple-950/40 px-3 py-1 text-xs font-semibold text-purple-300 mb-3">
              <Sparkles className="h-3.5 w-3.5 text-purple-400" />
              <span>Venture Lab</span>
            </div>

            {isPageHeader ? (
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-display text-balance">
                Building the next generation of <br className="hidden sm:block" />
                African startups.
              </h1>
            ) : (
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-display text-balance">
                Building the next generation of <br className="hidden sm:block" />
                African startups.
              </h2>
            )}

            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              We partner with founders from idea to Series A — design, engineering, growth and investor readiness under one roof.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 self-start md:self-auto">
            <button
              onClick={onOpenBooking}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-500 hover:bg-purple-400 text-slate-950 font-bold px-5 py-3 text-xs transition-colors cursor-pointer"
            >
              <span>Book Investor Consultation</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onExploreVentureLab}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-purple-500/40 bg-purple-950/30 hover:bg-purple-900/40 text-purple-200 font-semibold px-5 py-3 text-xs transition-colors cursor-pointer"
            >
              <span>Explore Venture Lab</span>
            </button>
          </div>
        </div>

        {/* 6 Venture Offerings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VENTURE_LAB_OFFERINGS.map((item, idx) => (
            <div
              key={item.title}
              className="rounded-2xl bg-slate-900/80 border border-slate-800 p-6 flex flex-col justify-between hover:border-purple-500/50 hover:bg-slate-900 transition-all text-left group"
            >
              <div>
                <div className="h-10 w-10 rounded-xl bg-purple-950/80 border border-purple-500/30 flex items-center justify-center text-purple-400 mb-4 group-hover:scale-105 transition-transform">
                  {getIcon(item.icon)}
                </div>

                <h3 className="text-lg font-bold text-white font-display mb-2 group-hover:text-purple-300 transition-colors">
                  {item.title}
                </h3>

                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                  {item.desc}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between text-xs text-purple-400 font-medium">
                <span>Tailored for African Founders</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </div>
          ))}
        </div>

        {/* Founder Partnership Metrics Strip */}
        <div className="mt-12 rounded-2xl bg-gradient-to-r from-purple-950/60 via-slate-900 to-indigo-950/60 border border-purple-500/30 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-left">
          <div>
            <div className="text-xs font-mono text-purple-400 font-semibold uppercase tracking-wider mb-1">
              Co-Founder As A Service
            </div>
            <div className="text-base sm:text-lg font-bold text-white">
              Are you an African founder seeking technical execution or seed funding?
            </div>
            <p className="text-xs text-slate-300 mt-1">
              We provide technical equity partnerships and investor matching across East, West, and Southern Africa.
            </p>
          </div>

          <button
            onClick={onOpenBooking}
            className="rounded-xl bg-white hover:bg-slate-100 text-slate-950 font-bold px-6 py-3 text-xs whitespace-nowrap cursor-pointer transition-colors shadow-sm"
          >
            Apply for Founder Lab
          </button>
        </div>

        {/* 4-Stage Venture Engineering Roadmap (Solves Low Word Count) */}
        <div className="mt-16 text-left">
          <div className="text-xs font-mono font-bold uppercase tracking-widest text-purple-400 mb-2">
            The Co-Founder Operating Model
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold font-display text-white mb-8">
            How Thinkers Arcade deploys engineering sweat-equity for African startups:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="text-xs font-mono text-purple-400 font-bold mb-2">PHASE 01 · 10 DAYS</div>
              <h4 className="text-sm font-bold text-white mb-2 font-display">Commercial &amp; System Scoping</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                We stress-test unit economics, regulatory requirements (ODPC, CBK, KRA eTIMS), user journey wireframes, and database relational schemas before writing a single line of production code.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="text-xs font-mono text-purple-400 font-bold mb-2">PHASE 02 · 30 DAYS</div>
              <h4 className="text-sm font-bold text-white mb-2 font-display">Rapid MVP Engineering</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Our senior software developers build a clean Flutter or React Native mobile app and Next.js backend with local SQLite sync, secure JWT authentication, and administrative dashboards.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="text-xs font-mono text-purple-400 font-bold mb-2">PHASE 03 · 14 DAYS</div>
              <h4 className="text-sm font-bold text-white mb-2 font-display">Payment Rails &amp; Pilot Launch</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Direct integration with Safaricom Daraja STK push (Paybill / Till), bank settlement webhooks, and SMS notifications. Deployment to Google Play Store and Apple App Store.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="text-xs font-mono text-purple-400 font-bold mb-2">PHASE 04 · ONGOING</div>
              <h4 className="text-sm font-bold text-white mb-2 font-display">Investor Deck &amp; Scaling</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                We prepare clean technical architecture documentation, GitHub code audit rooms, and traction telemetry for institutional angel syndicates and venture capital funds across Africa.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
